// Date formatting utilities

export const formatDate = (date: Date | string, locale: string = 'de'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // German format: DD.MM.YYYY HH:mm
  if (locale === 'de') {
    const day = String(dateObj.getDate()).padStart(2, '0')
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const year = dateObj.getFullYear()
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
  }

  // English format: MM/DD/YYYY HH:mm
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  const year = dateObj.getFullYear()
  const hours = String(dateObj.getHours()).padStart(2, '0')
  const minutes = String(dateObj.getMinutes()).padStart(2, '0')
  return `${month}/${day}/${year} ${hours}:${minutes}`
}

export const formatDateTime = (dateString: string, locale: string = 'de-DE'): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatTime = (dateString: string, locale: string = 'de-DE'): string => {
  const date = new Date(dateString)
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

// German format: DD.MM.YYYY HH:mm
export const formatDateTimeGerman = (dateString: string): string => {
  const date = new Date(dateString)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year} ${hours}:${minutes}`
}

// English format: MM/DD/YYYY HH:mm
export const formatDateTimeEnglish = (dateString: string): string => {
  const date = new Date(dateString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${month}/${day}/${year} ${hours}:${minutes}`
}

// PIN validation
export const isValidPin = (pin: string): boolean => {
  const pinNum = parseInt(pin, 10)
  return /^\d{5}$/.test(pin) && pinNum >= 10000 && pinNum <= 99999
}

// Age validation for children
export const isValidChildAge = (age: number): boolean => {
  return age >= 0 && age <= 17
}

// Debounce function for search/filter
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Local storage helpers
export const storage = {
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch {
      return defaultValue
    }
  },
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.error('Error saving to localStorage:', error)
    }
  },
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error('Error removing from localStorage:', error)
    }
  },
}

// Error message helpers
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'Ein unbekannter Fehler ist aufgetreten'
}

// RSVP status helpers
export const getRsvpStatusColor = (status: 'unknown' | 'coming' | 'not_coming'): string => {
  switch (status) {
    case 'coming':
      return 'green'
    case 'not_coming':
      return 'red'
    case 'unknown':
    default:
      return 'gray'
  }
}

export const getRsvpStatusLabel = (
  status: 'unknown' | 'coming' | 'not_coming',
  t: (key: string) => string
): string => {
  switch (status) {
    case 'coming':
      return t('Kommt')
    case 'not_coming':
      return t('Kommt nicht')
    case 'unknown':
    default:
      return t('Unbekannt')
  }
}

/**
 * Builds the guest PIN/RSVP URL used for QR-codes and printed links.
 * Uses the current origin in the browser and falls back to a relative URL in non-browser contexts.
 */
export const buildGuestPinUrl = (pin?: string): string => {
  const basePath = pin ? `/guest/logged_in/${pin}/invitation` : '/guest/pin'

  if (typeof window === 'undefined' || !window.location?.origin) {
    return basePath
  }

  return `${window.location.origin}${basePath}`
}

// Photo validation constants
export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const

export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
export const MAX_PHOTOS_PER_GUEST = 100

/**
 * Validates a photo file before upload
 * @param file - The file to validate
 * @param currentPhotoCount - Current number of photos for the guest
 * @returns Error message if validation fails, null if valid
 */
export const validatePhotoUpload = (file: File, currentPhotoCount: number): string | null => {
  // Check file type
  if (!ALLOWED_MIME_TYPES.includes(file.type as any)) {
    return 'Ungültiger Dateityp. Bitte laden Sie JPEG, PNG, WebP oder HEIC Bilder hoch.'
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return 'Dateigröße überschreitet das 10MB-Limit. Bitte wählen Sie eine kleinere Datei.'
  }

  if (file.size === 0) {
    return 'Die Datei ist leer. Bitte wählen Sie ein gültiges Bild.'
  }

  // Check photo limit
  if (currentPhotoCount >= MAX_PHOTOS_PER_GUEST) {
    return 'Sie haben das Maximum von 100 Fotos erreicht. Bitte löschen Sie einige Fotos, um weitere hochzuladen.'
  }

  return null // Valid
}

/**
 * Generates thumbnail and full-size URLs for a photo using Supabase Storage
 * @param storagePath - Storage path of the photo
 * @param supabase - Supabase client instance
 * @returns Object with thumbnailUrl and fullSizeUrl
 */
export const generatePhotoUrls = (
  storagePath: string,
  supabase: any
): { thumbnailUrl: string; fullSizeUrl: string } => {
  // Thumbnail: 300x300px, quality 80, cover mode
  const { data: thumbnailData } = supabase.storage
    .from('wedding-photos')
    .getPublicUrl(storagePath, {
      transform: {
        width: 300,
        height: 300,
        resize: 'cover',
        quality: 80,
      },
    })

  // Full-size: max 1920px width, quality 85
  const { data: fullSizeData } = supabase.storage.from('wedding-photos').getPublicUrl(storagePath)

  return {
    thumbnailUrl: thumbnailData.publicUrl,
    fullSizeUrl: fullSizeData.publicUrl,
  }
}

/**
 * Formats file size in human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}
